import { useMemo, useState } from "react";
import {
  Receipt, Plus, Trash2, Loader2, AlertCircle, Check, X, Pencil, Copy, CalendarDays,
} from "lucide-react";
import {
  useAdminDietLeviesQuery,
  useAdminLevyEventsQuery,
  useAdminCreateDietLevyMutation,
  useAdminUpdateDietLevyMutation,
  useAdminDeleteDietLevyMutation,
  useAdminCopyDietLeviesMutation,
  useAdminUpgradeReviewDietsQuery,
} from "../api/upgradeApi";
import { describeApiError } from "../../../lib/apiError";

const naira = (n: number | null | undefined) =>
  n == null ? "—" : "₦" + Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 });

/**
 * Which routes a Member levy is charged on.
 *
 * A UI-only field: it is not stored. "Exams only" means the matured route is
 * priced at zero, which is already what drops a line from that route's bill —
 * so the scope and the money are one fact, not two that can drift apart.
 */
type RouteScope = "both" | "exams" | "matured";

type Draft = {
  label: string;
  amount: string;
  /** Per-route prices, as typed. Blank means "not priced per route". */
  amount_exams: string;
  amount_matured: string;
  routeScope: RouteScope;
  to_category_id: string;
  event_id: string;
};

const EMPTY: Draft = {
  label: "",
  amount: "",
  amount_exams: "",
  amount_matured: "",
  routeScope: "both",
  to_category_id: "",
  event_id: "",
};

const SCOPE_OPTIONS: { key: RouteScope; label: string; hint: string }[] = [
  { key: "both", label: "Both routes", hint: "Charged to every Member, at the price set for their route" },
  { key: "exams", label: "Exams only", hint: "Charged only to Members who came through the exams route (PCI)" },
  { key: "matured", label: "Matured only", hint: "Charged only to Members who came through the matured route (MRI)" },
];

const SCOPE_CHIP: Record<string, { text: string; tone: string }> = {
  exams: { text: "Exams route only", tone: "text-indigo-600" },
  matured: { text: "Matured route only", tone: "text-indigo-600" },
  neither: { text: "Charged on neither route", tone: "text-amber-600" },
};

const ROUTE_LABEL: Record<string, string> = {
  exams: "Exams route (PCI)",
  matured: "Matured route (MRI)",
};

const ROUTE_SHORT: Record<string, string> = { exams: "Exams", matured: "Matured" };

/**
 * The pre-investiture levies this review cycle charges.
 *
 * Each sitting resolves its own bill — an upgrading charge, a conference fee,
 * an investiture fee, a history book — so the charges live on the CYCLE rather
 * than in config, where they were keyed by upgrade route and therefore existed
 * for Members only. A recommended applicant settles the whole list in one
 * payment, and every levy that names an event books them a seat at it.
 *
 * Two things the screen has to make impossible to get wrong:
 *   · a Fellow levy landing on a Member's bill — hence the grade on every row,
 *     and the per-grade totals, which is the figure being checked against the
 *     resolution;
 *   · a levy that bills money and books nobody — hence the event column reading
 *     "no seat" rather than staying blank;
 *   · ONE amount standing in for two routes the Institute prices differently —
 *     a Probationer → Member upgrade runs the exams route (PCI) or the matured
 *     route (MRI), so a levy charged to Members asks for both prices and the
 *     per-grade total splits in two. A Fellow has no route and is unaffected.
 */
export default function DietLeviesCard({
  dietId,
  canAct,
  lockedReason,
}: {
  dietId: string;
  /** Only the cycle that is sitting takes changes — same rule as the rest. */
  canAct: boolean;
  lockedReason: string;
}) {
  const { data, isLoading, isError } = useAdminDietLeviesQuery(dietId, { skip: !dietId });
  const { data: eventsData } = useAdminLevyEventsQuery();
  // The other cycles, for "copy levies from…". The list endpoint takes no
  // argument.
  const { data: dietsData } = useAdminUpgradeReviewDietsQuery();

  const [create, { isLoading: creating }] = useAdminCreateDietLevyMutation();
  const [update, { isLoading: updating }] = useAdminUpdateDietLevyMutation();
  const [remove] = useAdminDeleteDietLevyMutation();
  const [copy, { isLoading: copying }] = useAdminCopyDietLeviesMutation();

  const levies: any[] = data?.data ?? [];
  const totals: any[] = data?.totals ?? [];
  const grades: any[] = data?.grades ?? [];
  const events: any[] = eventsData?.data ?? [];
  const otherDiets: any[] = (dietsData?.data ?? dietsData ?? []).filter(
    (d: any) => d?.id && d.id !== dietId,
  );

  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState<Draft>(EMPTY);
  const [copyFrom, setCopyFrom] = useState("");
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const total = useMemo(
    () => levies.reduce((sum, l) => sum + Number(l.amount ?? 0), 0),
    [levies],
  );

  const run = async (fn: () => Promise<any>, fallback: string) => {
    setMsg(null);
    try {
      const res: any = await fn();
      setMsg({ ok: true, text: res?.message ?? fallback });
      return true;
    } catch (e: any) {
      setMsg({ ok: false, text: describeApiError(e) || "That did not work." });
      return false;
    }
  };

  /* ── Which levies are priced per route ───────────────────────────────────
     The server says which grade runs routes and what they are called, so the
     screen does not carry a second copy of the rule that "Member" is the
     routed grade.

     SHOWN for the Member grade and for an all-grades levy — the latter lands on
     Members too, and may legitimately be priced per route while a Fellow takes
     the base amount. REQUIRED only for a levy charged to Members, which is the
     case where one figure would otherwise stand in for two different prices. */
  const routedId: number | null = (data as any)?.routed_category_id ?? null;
  const routes: string[] = (data as any)?.routes ?? ["exams", "matured"];

  const routesShown = (categoryId: string) =>
    routedId != null && (categoryId === "" || Number(categoryId) === Number(routedId));

  const routesRequired = (categoryId: string) =>
    routedId != null && categoryId !== "" && Number(categoryId) === Number(routedId);

  /**
   * The two route prices this draft actually saves.
   *
   * On a Member levy the SCOPE decides: a route the levy is not charged on is
   * sent as zero, which is what drops it from that route's bill. On an
   * all-grades levy there is no scope — both prices are optional, and blank
   * means every route pays the base amount.
   */
  const routePrices = (d: Draft): { exams: number | null; matured: number | null } => {
    const e = d.amount_exams.trim();
    const m = d.amount_matured.trim();

    if (!routesRequired(d.to_category_id)) {
      return {
        // Null, not 0 — "not priced per route" and "free on this route" are
        // different states.
        exams: e === "" ? null : Number(e),
        matured: m === "" ? null : Number(m),
      };
    }

    return {
      exams: d.routeScope === "matured" ? 0 : Number(e || 0),
      matured: d.routeScope === "exams" ? 0 : Number(m || 0),
    };
  };

  const body = (d: Draft) => {
    const { exams, matured } = routePrices(d);

    return {
      label: d.label.trim(),
      amount: Number(d.amount),
      amount_exams: exams,
      amount_matured: matured,
      to_category_id: d.to_category_id ? Number(d.to_category_id) : null,
      event_id: d.event_id || null,
    };
  };

  /* The price each route the levy IS charged on must carry, plus the server's
     own rule that an all-grades levy is priced for both routes or for neither.
     Checked here so the button is simply disabled rather than the save coming
     back 422. */
  const routePricesOk = (d: Draft) => {
    const e = d.amount_exams.trim();
    const m = d.amount_matured.trim();

    if (routesRequired(d.to_category_id)) {
      if (d.routeScope === "exams") return e !== "" && Number(e) > 0;
      if (d.routeScope === "matured") return m !== "" && Number(m) > 0;
      return e !== "" && m !== "" && Number(e) >= 0 && Number(m) >= 0;
    }

    return (e === "") === (m === "");
  };

  const valid = (d: Draft) =>
    d.label.trim().length > 1 && Number(d.amount) > 0 && routePricesOk(d);

  /** Why the save button is off, for the hint under the form. */
  const invalidReason = (d: Draft) => {
    if (d.label.trim().length <= 1) return "Give the levy a name.";
    if (!(Number(d.amount) > 0)) return "Enter the amount.";

    if (routesRequired(d.to_category_id)) {
      if (d.routeScope === "exams") return "Enter what the exams route pays for this levy.";
      if (d.routeScope === "matured") return "Enter what the matured route pays for this levy.";
      return "Enter a price for each route — or charge the levy to one route only.";
    }

    return "Price this levy for both routes or for neither.";
  };

  /** The scope a saved levy is in, for the edit row's selector. */
  const scopeOf = (l: any): RouteScope =>
    l?.route_scope === "exams" || l?.route_scope === "matured" ? l.route_scope : "both";

  // The 503 the API answers on a host that has the code but not the migration.
  const notMigrated = (data as any)?.message && levies.length === 0 && !isLoading;

  return (
    <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-emerald-50 border border-emerald-100 flex items-center justify-center">
            <Receipt className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <h2 className="text-lg font-black text-gray-900">Levies for this cycle</h2>
            <p className="text-xs font-semibold text-gray-500">
              What a recommended applicant pays before investiture — billed as one payment, and each
              levy with an event books them a seat at it.
            </p>
          </div>
        </div>
        {levies.length > 0 ? (
          <div className="text-right">
            <div className="text-[10px] font-black uppercase tracking-widest text-gray-400">
              All levies
            </div>
            <div className="text-xl font-black text-gray-900 tabular-nums">{naira(total)}</div>
          </div>
        ) : null}
      </div>

      {msg ? (
        <div
          className={`mt-4 flex gap-2 p-3 rounded-2xl text-[12px] font-semibold border ${
            msg.ok
              ? "bg-emerald-50 border-emerald-200 text-emerald-800"
              : "bg-rose-50 border-rose-200 text-rose-700"
          }`}
        >
          {msg.ok ? (
            <Check className="w-4 h-4 shrink-0 mt-0.5" />
          ) : (
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          )}
          <div>{msg.text}</div>
        </div>
      ) : null}

      {isLoading ? (
        <div className="mt-6 h-24 bg-gray-50 rounded-2xl animate-pulse" />
      ) : isError || notMigrated ? (
        <p className="mt-6 text-sm font-semibold text-amber-600">
          {String((data as any)?.message || "The levies for this cycle could not be loaded.")}
        </p>
      ) : (
        <>
          {/* Per-grade totals. One cycle bills Fellows and Members differently,
              so the only total that means anything to an applicant is their
              grade's — the shared levies plus their own. */}
          {totals.length > 0 ? (
            <div className="mt-5 flex flex-wrap gap-2">
              {totals.map((t: any) => (
                <div
                  key={String(t.to_category_id)}
                  className="px-3 py-2 rounded-2xl border border-gray-100 bg-gray-50/60"
                >
                  <div className="text-[10px] font-black uppercase tracking-widest text-gray-400">
                    {t.grade || "—"} · {t.applicants} in cycle
                  </div>

                  {/* A routed grade has no single total: the two routes are
                      priced differently, so one figure would be wrong for at
                      least one of them. The head count sits under each, which
                      is what the resolution is checked against. */}
                  {t.routed && t.route_totals ? (
                    <div className="mt-0.5 space-y-0.5">
                      {routes.map((r: string) => {
                        const rt = t.route_totals[r];
                        if (!rt) return null;

                        return (
                          <div key={r} className="flex items-baseline gap-2">
                            <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 w-14">
                              {ROUTE_SHORT[r] ?? r}
                            </span>
                            <span className="text-sm font-black text-gray-900 tabular-nums">
                              {naira(rt.total)}
                            </span>
                            <span className="text-[11px] font-bold text-gray-400">
                              {rt.applicants} applicant{rt.applicants === 1 ? "" : "s"}
                            </span>
                            {rt.excluded?.length ? (
                              <span
                                className="text-[10px] font-bold text-gray-400"
                                title={`Not charged on this route: ${rt.excluded.join(", ")}`}
                              >
                                −{rt.excluded.length}
                              </span>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-sm font-black text-gray-900 tabular-nums">
                      {naira(t.total)}
                      <span className="ml-1 text-[11px] font-bold text-gray-400">
                        over {t.levies} levy{t.levies === 1 ? "" : "ies"}
                      </span>
                    </div>
                  )}

                  {/* Billed to a routed grade with no route prices on it, so
                      both routes are quietly on the base amount — which is the
                      state every levy was in before route prices existed. */}
                  {t.needs_route_prices?.length ? (
                    <div className="mt-1 text-[10px] font-bold text-amber-600 max-w-[16rem]">
                      Both routes pay the base amount on:{" "}
                      {t.needs_route_prices.join(", ")}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          ) : null}

          {levies.length === 0 ? (
            <p className="mt-5 text-sm text-gray-400">
              No levies set for this cycle yet. Until one is added, a recommended applicant in this
              cycle is billed from the route-based schedule in config — which has no fellowship
              charges at all, so a Fellow has nothing to pay and cannot be invested.
            </p>
          ) : (
            <div className="mt-5 border rounded-2xl overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] font-black uppercase tracking-widest text-gray-400 border-b">
                    <th className="px-4 py-2">Levy</th>
                    <th className="px-4 py-2">Grade</th>
                    <th className="px-4 py-2">Books a seat at</th>
                    <th className="px-4 py-2 text-right">Amount</th>
                    <th className="px-4 py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {levies.map((l: any) =>
                    editing === l.id ? (
                      <tr key={l.id} className="bg-indigo-50/40">
                        <td className="px-4 py-2">
                          <input
                            value={editDraft.label}
                            onChange={(e) => setEditDraft((d) => ({ ...d, label: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm"
                          />
                        </td>
                        <td className="px-4 py-2">
                          <select
                            value={editDraft.to_category_id}
                            onChange={(e) =>
                              setEditDraft((d) => ({ ...d, to_category_id: e.target.value }))
                            }
                            className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm bg-white"
                          >
                            <option value="">Every grade</option>
                            {grades.map((g: any) => (
                              <option key={g.id} value={g.id}>
                                {g.name}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-4 py-2">
                          <select
                            value={editDraft.event_id}
                            onChange={(e) =>
                              setEditDraft((d) => ({ ...d, event_id: e.target.value }))
                            }
                            className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm bg-white"
                          >
                            <option value="">No seat — money only</option>
                            {events.map((ev: any) => (
                              <option key={ev.id} value={ev.id}>
                                {ev.title}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-4 py-2">
                          {/* Base, then the two route prices when the grade
                              selected in THIS row runs routes — stacked in the
                              one cell so the table keeps its shape. */}
                          <div className="space-y-1">
                            <div className="flex items-center gap-1.5 justify-end">
                              <span className="text-[9px] font-black uppercase tracking-wider text-gray-400 w-12 text-right">
                                Base
                              </span>
                              <input
                                type="number"
                                min={0}
                                value={editDraft.amount}
                                onChange={(e) =>
                                  setEditDraft((d) => ({ ...d, amount: e.target.value }))
                                }
                                className="w-28 px-3 py-1.5 border border-gray-200 rounded-xl text-sm text-right tabular-nums"
                              />
                            </div>

                            {/* Which routes this levy is charged on. A compact
                                select here rather than the add form's chips —
                                the cell has one column to work in. */}
                            {routesRequired(editDraft.to_category_id) ? (
                              <div className="flex items-center gap-1.5 justify-end">
                                <span className="text-[9px] font-black uppercase tracking-wider text-gray-400 w-12 text-right">
                                  Routes
                                </span>
                                <select
                                  value={editDraft.routeScope}
                                  onChange={(e) =>
                                    setEditDraft((d) => ({
                                      ...d,
                                      routeScope: e.target.value as RouteScope,
                                    }))
                                  }
                                  className="w-28 px-2 py-1.5 border border-gray-200 rounded-xl text-[11px] font-bold bg-white"
                                >
                                  {SCOPE_OPTIONS.map((o) => (
                                    <option key={o.key} value={o.key}>
                                      {o.label}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            ) : null}

                            {routesShown(editDraft.to_category_id)
                              ? routes
                                  .filter(
                                    (r: string) =>
                                      !routesRequired(editDraft.to_category_id) ||
                                      editDraft.routeScope === "both" ||
                                      editDraft.routeScope === r,
                                  )
                                  .map((r: string) => (
                                    <div key={r} className="flex items-center gap-1.5 justify-end">
                                      <span className="text-[9px] font-black uppercase tracking-wider text-gray-400 w-12 text-right">
                                        {ROUTE_SHORT[r] ?? r}
                                      </span>
                                      <input
                                        type="number"
                                        min={0}
                                        placeholder="—"
                                        value={
                                          r === "exams"
                                            ? editDraft.amount_exams
                                            : editDraft.amount_matured
                                        }
                                        onChange={(e) =>
                                          setEditDraft((d) =>
                                            r === "exams"
                                              ? { ...d, amount_exams: e.target.value }
                                              : { ...d, amount_matured: e.target.value },
                                          )
                                        }
                                        className="w-28 px-3 py-1.5 border border-gray-200 rounded-xl text-sm text-right tabular-nums"
                                      />
                                    </div>
                                  ))
                              : null}
                          </div>
                        </td>
                        <td className="px-4 py-2 text-right whitespace-nowrap">
                          <button
                            onClick={async () => {
                              const done = await run(
                                () =>
                                  update({
                                    id: dietId,
                                    levyId: l.id,
                                    ...body(editDraft),
                                  }).unwrap(),
                                "Levy updated.",
                              );
                              if (done) setEditing(null);
                            }}
                            disabled={!valid(editDraft) || updating}
                            className="p-1.5 rounded-lg border border-emerald-200 text-emerald-700 hover:bg-emerald-50 disabled:opacity-40"
                          >
                            {updating ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <Check className="w-3.5 h-3.5" />
                            )}
                          </button>
                          <button
                            onClick={() => setEditing(null)}
                            className="ml-1.5 p-1.5 rounded-lg border border-gray-100 text-gray-400 hover:text-gray-700"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    ) : (
                      <tr key={l.id} className="hover:bg-gray-50/60">
                        <td className="px-4 py-2.5">
                          <div className="font-semibold text-gray-900">{l.label}</div>
                          <div className="text-[11px] text-gray-400 font-mono">{l.key}</div>
                        </td>
                        <td className="px-4 py-2.5">
                          {l.grade ? (
                            <span className="inline-flex px-2 py-0.5 rounded-full border border-indigo-200 bg-indigo-50 text-indigo-700 text-[11px] font-bold">
                              {l.grade}
                            </span>
                          ) : (
                            <span className="text-[11px] font-semibold text-gray-400">
                              Every grade
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2.5">
                          {l.event_title ? (
                            <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-700">
                              <CalendarDays className="w-3.5 h-3.5" />
                              {l.event_title}
                            </span>
                          ) : (
                            <span className="text-[11px] font-semibold text-gray-400">
                              No seat — money only
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-right font-bold tabular-nums text-gray-900">
                          {/* Priced per route: the two figures are what is
                              actually charged, so they lead and the base is
                              shown only as the fallback it is. */}
                          {l.amount_exams != null || l.amount_matured != null ? (
                            <div className="space-y-0.5">
                              {/* Charged on one route only: the one price, and
                                  the scope said in words. Showing "Matured ₦0"
                                  reads as free rather than not applicable. */}
                              {SCOPE_CHIP[l.route_scope] ? (
                                <>
                                  {l.route_scope !== "neither" ? (
                                    <div>
                                      {naira(
                                        Number(
                                          l.route_scope === "exams"
                                            ? l.amount_exams
                                            : l.amount_matured,
                                        ),
                                      )}
                                    </div>
                                  ) : null}
                                  <div
                                    className={`text-[9px] font-black uppercase tracking-wider ${
                                      SCOPE_CHIP[l.route_scope].tone
                                    }`}
                                  >
                                    {SCOPE_CHIP[l.route_scope].text}
                                  </div>
                                </>
                              ) : (
                                routes.map((r: string) => {
                                  const v = r === "exams" ? l.amount_exams : l.amount_matured;

                                  return (
                                    <div
                                      key={r}
                                      className="flex items-baseline justify-end gap-1.5"
                                    >
                                      <span className="text-[9px] font-black uppercase tracking-wider text-gray-400">
                                        {ROUTE_SHORT[r] ?? r}
                                      </span>
                                      <span className={Number(v) > 0 ? "" : "text-gray-400"}>
                                        {v == null ? naira(Number(l.amount)) : naira(Number(v))}
                                      </span>
                                    </div>
                                  );
                                })
                              )}
                            </div>
                          ) : (
                            <>
                              {naira(Number(l.amount))}
                              {/* Charged to Members with no route prices on it,
                                  so both routes pay this one figure. */}
                              {l.needs_route_prices ? (
                                <div
                                  className="text-[9px] font-black uppercase tracking-wider text-amber-600"
                                  title="This levy is charged to Members, who upgrade by one of two routes priced differently. Edit it to set a price for each."
                                >
                                  both routes
                                </div>
                              ) : null}
                            </>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-right whitespace-nowrap">
                          <button
                            onClick={() => {
                              setEditing(l.id);
                              setEditDraft({
                                label: l.label ?? "",
                                amount: String(l.amount ?? ""),
                                // Blank, not "0", where the levy carries no
                                // route price — the two are different states.
                                amount_exams:
                                  l.amount_exams == null ? "" : String(l.amount_exams),
                                amount_matured:
                                  l.amount_matured == null ? "" : String(l.amount_matured),
                                routeScope: scopeOf(l),
                                to_category_id: l.to_category_id ? String(l.to_category_id) : "",
                                event_id: l.event_id ?? "",
                              });
                            }}
                            disabled={!canAct}
                            title={canAct ? "Edit this levy" : lockedReason}
                            className="p-1.5 rounded-lg border border-gray-100 text-gray-400 hover:text-indigo-600 hover:border-indigo-200 disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => {
                              if (window.confirm(`Remove "${l.label}" from this cycle's levies?`)) {
                                run(
                                  () => remove({ id: dietId, levyId: l.id }).unwrap(),
                                  "Levy removed.",
                                );
                              }
                            }}
                            disabled={!canAct}
                            title={canAct ? "Remove this levy" : lockedReason}
                            className="ml-1.5 p-1.5 rounded-lg border border-gray-100 text-gray-400 hover:text-rose-600 hover:border-rose-200 disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    ),
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Add one. An inline row rather than a dialog: a cycle's bill is
              entered as a list, six charges in a sitting, and a modal per
              charge is six dialogs. */}
          {canAct ? (
            adding ? (
              <div className="mt-4 p-4 rounded-2xl border border-indigo-200 bg-indigo-50/40 space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <div className="md:col-span-2">
                    <label className="text-[10px] font-black uppercase tracking-widest text-gray-400">
                      Levy
                    </label>
                    <input
                      autoFocus
                      value={draft.label}
                      onChange={(e) => setDraft((d) => ({ ...d, label: e.target.value }))}
                      placeholder="e.g. Investiture Fee"
                      className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-xl text-sm bg-white"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-black uppercase tracking-widest text-gray-400">
                      Amount (₦)
                    </label>
                    <input
                      type="number"
                      min={0}
                      value={draft.amount}
                      onChange={(e) => setDraft((d) => ({ ...d, amount: e.target.value }))}
                      placeholder="250000"
                      className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-xl text-sm text-right tabular-nums bg-white"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-black uppercase tracking-widest text-gray-400">
                      Grade
                    </label>
                    <select
                      value={draft.to_category_id}
                      onChange={(e) => setDraft((d) => ({ ...d, to_category_id: e.target.value }))}
                      className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-xl text-sm bg-white"
                    >
                      <option value="">Every grade</option>
                      {grades.map((g: any) => (
                        <option key={g.id} value={g.id}>
                          {g.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* ── The two routes ─────────────────────────────────────────
                    Appears the moment the grade chosen can reach a Member,
                    because that is the only upgrade with a route. Required for
                    a levy charged to Members: the Institute prices the exams
                    route (PCI) and the matured route (MRI) differently, and one
                    figure for both is what this replaces. */}
                {routesShown(draft.to_category_id) ? (
                  <div className="p-3 rounded-2xl border border-amber-200 bg-amber-50/60">
                    <div className="text-[10px] font-black uppercase tracking-widest text-amber-700">
                      {routesRequired(draft.to_category_id)
                        ? "Charged on which route"
                        : "Price per upgrade route · optional"}
                    </div>

                    {/* A Member levy says which routes it belongs to FIRST, then
                        prices them. Some levies are charged to one route only —
                        a fee the exams candidates pay and the matured ones do
                        not — and that is a property of the levy, not a zero the
                        admin has to know to type. */}
                    {routesRequired(draft.to_category_id) ? (
                      <>
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {SCOPE_OPTIONS.map((o) => (
                            <button
                              key={o.key}
                              type="button"
                              title={o.hint}
                              onClick={() => setDraft((d) => ({ ...d, routeScope: o.key }))}
                              className={`px-3 py-1.5 rounded-xl border text-xs font-bold transition ${
                                draft.routeScope === o.key
                                  ? "bg-gray-900 text-white border-gray-900"
                                  : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
                              }`}
                            >
                              {o.label}
                            </button>
                          ))}
                        </div>

                        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {routes
                            // Only the routes this levy is actually charged on —
                            // a price field for a route that is not charged is a
                            // field whose value is thrown away.
                            .filter(
                              (r: string) =>
                                draft.routeScope === "both" || draft.routeScope === r,
                            )
                            .map((r: string) => (
                              <div key={r}>
                                <label className="text-[10px] font-bold text-gray-500">
                                  {ROUTE_LABEL[r] ?? r}
                                </label>
                                <input
                                  type="number"
                                  min={0}
                                  value={r === "exams" ? draft.amount_exams : draft.amount_matured}
                                  onChange={(e) =>
                                    setDraft((d) =>
                                      r === "exams"
                                        ? { ...d, amount_exams: e.target.value }
                                        : { ...d, amount_matured: e.target.value },
                                    )
                                  }
                                  placeholder={draft.amount || "0"}
                                  className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-xl text-sm text-right tabular-nums bg-white"
                                />
                              </div>
                            ))}
                        </div>

                        <p className="mt-2 text-[11px] font-semibold text-gray-600">
                          {draft.routeScope === "both"
                            ? "Both routes are billed this levy, each at its own price."
                            : `Only Members on the ${
                                draft.routeScope === "exams" ? "exams" : "matured"
                              } route are billed this levy. It does not appear on the other route's bill at all.`}
                        </p>
                      </>
                    ) : (
                      <>
                        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {routes.map((r: string) => (
                            <div key={r}>
                              <label className="text-[10px] font-bold text-gray-500">
                                {ROUTE_LABEL[r] ?? r}
                              </label>
                              <input
                                type="number"
                                min={0}
                                value={r === "exams" ? draft.amount_exams : draft.amount_matured}
                                onChange={(e) =>
                                  setDraft((d) =>
                                    r === "exams"
                                      ? { ...d, amount_exams: e.target.value }
                                      : { ...d, amount_matured: e.target.value },
                                  )
                                }
                                placeholder={draft.amount || "0"}
                                className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-xl text-sm text-right tabular-nums bg-white"
                              />
                            </div>
                          ))}
                        </div>

                        <p className="mt-2 text-[11px] font-semibold text-gray-600">
                          Left blank, every route pays the amount above. Enter <b>0</b> on a route
                          this levy is not charged on. These apply to Members only — a Fellow has no
                          route and pays the amount above.
                        </p>
                      </>
                    )}
                  </div>
                ) : null}

                <div>
                  <label className="text-[10px] font-black uppercase tracking-widest text-gray-400">
                    Books a seat at
                  </label>
                  <select
                    value={draft.event_id}
                    onChange={(e) => setDraft((d) => ({ ...d, event_id: e.target.value }))}
                    className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-xl text-sm bg-white"
                  >
                    <option value="">No seat — money only (a book, a charge)</option>
                    {events.map((ev: any) => (
                      <option key={ev.id} value={ev.id}>
                        {ev.title}
                        {ev.start_date ? ` · ${ev.start_date}` : ""}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-[11px] font-semibold text-gray-500">
                    Paying the bundle registers the applicant for this event, marked paid, with
                    their access card and attendance ready at the door.
                  </p>
                </div>

                <div className="flex flex-wrap items-center justify-end gap-2">
                  {/* Why the button is off, rather than a disabled button with
                      nothing to explain it. */}
                  {!valid(draft) && draft.label.trim().length > 1 ? (
                    <span className="mr-auto text-[11px] font-bold text-amber-600 max-w-md">
                      {invalidReason(draft)}
                    </span>
                  ) : null}
                  <button
                    onClick={() => {
                      setAdding(false);
                      setDraft(EMPTY);
                    }}
                    className="px-4 py-2 rounded-xl text-xs font-bold text-gray-500 hover:bg-white"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                      const done = await run(
                        () => create({ id: dietId, ...body(draft) }).unwrap(),
                        "Levy added.",
                      );
                      if (done) setDraft(EMPTY);
                    }}
                    disabled={!valid(draft) || creating}
                    className="inline-flex items-center gap-2 px-5 py-2 rounded-xl bg-indigo-600 text-white text-xs font-bold hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {creating ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Plus className="w-3.5 h-3.5" />
                    )}
                    Add levy
                  </button>
                </div>
              </div>
            ) : (
              <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                <button
                  onClick={() => setAdding(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-indigo-200 bg-indigo-50 text-indigo-700 text-xs font-bold hover:bg-indigo-100"
                >
                  <Plus className="w-3.5 h-3.5" /> Add a levy
                </button>

                {/* Copy last cycle's bill and amend it — how a sitting actually
                    sets its levies. */}
                {otherDiets.length > 0 ? (
                  <div className="flex items-center gap-2">
                    <select
                      value={copyFrom}
                      onChange={(e) => setCopyFrom(e.target.value)}
                      className="px-3 py-2 border border-gray-200 rounded-xl text-xs bg-white"
                    >
                      <option value="">Copy levies from…</option>
                      {otherDiets.map((d: any) => (
                        <option key={d.id} value={d.id}>
                          {d.title}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={async () => {
                        const done = await run(
                          () => copy({ id: dietId, from_diet_id: copyFrom }).unwrap(),
                          "Levies copied.",
                        );
                        if (done) setCopyFrom("");
                      }}
                      disabled={!copyFrom || copying}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 text-gray-600 text-xs font-bold hover:bg-gray-50 disabled:opacity-40"
                    >
                      {copying ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Copy className="w-3.5 h-3.5" />
                      )}
                      Copy
                    </button>
                  </div>
                ) : null}
              </div>
            )
          ) : (
            <p className="mt-4 text-[11px] font-semibold text-amber-600">{lockedReason}</p>
          )}
        </>
      )}
    </div>
  );
}
